/**
 * /admin/credits — add Zen Credits to a user's wallet.
 *
 * Search a user, type an amount and a reason, press Add. The backend
 * writes an ADMIN_ADJUST ledger row through wallet.services.credit_wallet
 * (not counted as revenue). Each submit carries its own request id, so a
 * double click or a retried request grants once.
 */
import { useEffect, useRef, useState } from "react";
import { RefreshCw, Loader2, Search, Coins, X } from "lucide-react";
import {
  searchCreditUsers, listCreditGrants, grantCredits,
} from "../../api/admin.extras.api";
import { useToast } from "../../components/common/ToastHost";

const QUICK_AMOUNTS = [50, 100, 250, 500, 1000];

function newRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function fmtDate(iso) {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AdminCredits() {
  const toast = useToast();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const [credits, setCredits] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const requestIdRef = useRef(newRequestId());

  const [grants, setGrants] = useState([]);
  const [loadingGrants, setLoadingGrants] = useState(true);

  // Debounced user search.
  useEffect(() => {
    if (selected) return undefined;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await searchCreditUsers(q);
        setResults(data.results || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "User search failed.");
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selected]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadGrants(); }, []);

  async function loadGrants() {
    setLoadingGrants(true);
    try {
      const { data } = await listCreditGrants();
      setGrants(data.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load recent grants.");
    } finally {
      setLoadingGrants(false);
    }
  }

  function pick(u) {
    setSelected(u);
    setResults([]);
    requestIdRef.current = newRequestId();
  }

  function clearUser() {
    setSelected(null);
    setQuery("");
    setCredits("");
    setNotes("");
  }

  const amount = Number(credits);
  const canSubmit = selected && Number.isInteger(amount) && amount > 0
    && notes.trim().length > 0 && !saving;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    if (!confirm(`Add ${amount.toLocaleString("en-IN")} Zen Credits to ${selected.email}?`)) return;
    setSaving(true);
    try {
      const { data } = await grantCredits({
        userId: selected.id, credits: amount, notes: notes.trim(),
        requestId: requestIdRef.current,
      });
      toast.success(
        `Added ${amount.toLocaleString("en-IN")} ZC to ${selected.email}. `
        + `New balance ${data.balance_after.toLocaleString("en-IN")} ZC.`,
      );
      setSelected(data.user);
      setCredits("");
      setNotes("");
      requestIdRef.current = newRequestId();
      loadGrants();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Couldn't add credits.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Credits</h1>
          <p className="text-sm text-text-secondary mt-1">
            Add Zen Credits to a user's wallet. Logged as an admin adjustment, not revenue.
          </p>
        </div>
        <button onClick={loadGrants} className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5">
          <RefreshCw className={`w-4 h-4 ${loadingGrants ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <form onSubmit={submit} className="rounded-md border border-border-default bg-bg-primary p-5 space-y-4 max-w-2xl">
        <div>
          <label className="block text-sm font-medium mb-1.5">User</label>
          {selected ? (
            <div className="flex items-center justify-between rounded-md border border-border-default bg-bg-secondary px-3 py-2">
              <div className="text-sm">
                <div className="font-medium">{selected.name}</div>
                <div className="text-text-secondary">
                  {selected.email} · #{selected.id}
                  {!selected.is_active && <span className="ml-2 text-amber-600">inactive</span>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm">
                  Balance <strong>{selected.balance.toLocaleString("en-IN")} ZC</strong>
                </span>
                <button type="button" onClick={clearUser} className="p-1 rounded hover:bg-bg-primary" aria-label="Change user">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-text-secondary" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by email, name or user id"
                className="w-full pl-9 pr-9 py-2 text-sm rounded-md border border-border-default bg-bg-primary"
              />
              {searching && <Loader2 className="w-4 h-4 absolute right-3 top-2.5 animate-spin text-text-secondary" />}
              {results.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border-default bg-bg-primary shadow-lg divide-y divide-border-default">
                  {results.map((u) => (
                    <li key={u.id}>
                      <button type="button" onClick={() => pick(u)} className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary flex items-center justify-between">
                        <span>
                          <span className="font-medium">{u.name}</span>
                          <span className="text-text-secondary"> · {u.email} · #{u.id}</span>
                          {!u.is_active && <span className="ml-2 text-amber-600">inactive</span>}
                        </span>
                        <span className="text-text-secondary">{u.balance.toLocaleString("en-IN")} ZC</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p className="text-xs text-text-secondary mt-1">No users match.</p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Credits to add</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number" min={1} step={1}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="e.g. 100"
              className="w-36 px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary"
            />
            {QUICK_AMOUNTS.map((n) => (
              <button key={n} type="button" onClick={() => setCredits(String(n))}
                className="px-2.5 py-1.5 text-xs rounded-md border border-border-default hover:bg-bg-secondary">
                +{n}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1.5">Reason</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            placeholder="e.g. Goodwill for failed AI commentary"
            className="w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary"
          />
        </div>

        <button type="submit" disabled={!canSubmit}
          className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
          Add credits
        </button>
      </form>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Recent admin grants</h2>
        <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
              <tr>
                <th className="px-4 py-2.5">When</th>
                <th className="px-4 py-2.5">User</th>
                <th className="px-4 py-2.5 text-right">Credits</th>
                <th className="px-4 py-2.5 text-right">Balance after</th>
                <th className="px-4 py-2.5">Reason</th>
                <th className="px-4 py-2.5">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {loadingGrants && grants.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary">
                  <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
                </td></tr>
              )}
              {!loadingGrants && grants.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary">No admin grants yet.</td></tr>
              )}
              {grants.map((g) => (
                <tr key={g.id}>
                  <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(g.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{g.user.name}</div>
                    <div className="text-xs text-text-secondary">{g.user.email}</div>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-medium ${g.credits >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {g.credits >= 0 ? "+" : ""}{g.credits.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-2.5 text-right">{g.balance_after.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5 max-w-xs truncate" title={g.notes}>{g.notes || "—"}</td>
                  <td className="px-4 py-2.5 text-text-secondary">{g.actor || "system"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
